import React, { useState, useMemo, useEffect } from "react";
import { C, getPerms } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, TransactionsTab, useToast
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, Legend
} from "recharts";
import {
  Package, Car, Smartphone, Wrench, ClipboardCheck, FileText,
  Bell, Plus, Search, AlertTriangle, CheckCircle, Clock,
  Calendar, Eye, Printer, Download, ArrowLeftRight,
  DollarSign, BarChart3, PieChart as PieChartIcon,
  Activity, Boxes, ShieldCheck, ShieldAlert,
  FileCheck, RefreshCw, Award
} from "lucide-react";
import {
  getAssetsAssets, createAssetsAsset,
  getAssetsCustody, createAssetsCustody, updateAssetsCustody,
  getAssetsSims,
  getAssetsVehicles,
  getAssetsMaintenance,
  getAssetsInventory,
} from "./supabaseClient";

/* ═════════════════════════════════════════════
   ✦ Row mappers — snake_case (DB) → camelCase (UI)
   ═════════════════════════════════════════════ */
function mapAssetRow(r) {
  return {
    id: r.id,
    code: r.code || "",
    name: r.name || "",
    category: r.category || "tech",
    brand: r.brand || "",
    model: r.model || "",
    serialNumber: r.serial_number || "",
    purchaseDate: r.purchase_date || "",
    purchasePrice: Number(r.purchase_price) || 0,
    warrantyEnd: r.warranty_end || "",
    location: r.location || "",
    status: r.status || "available",
    currentHolder: r.current_holder || null,
    notes: r.notes || "",
  };
}

export function mapCustodyRow(r) {
  return {
    id: r.id,
    assetId: r.asset_id || "",
    assetName: r.asset_name || "",
    employeeId: r.employee_id || "",
    employeeName: r.employee_name || "",
    handoverDate: r.handover_date || "",
    expectedReturnDate: r.expected_return_date || "",
    actualReturnDate: r.actual_return_date || "",
    condition: r.condition || "سليم",
    handoverDocRef: r.handover_doc_ref || "",
    returnDocRef: r.return_doc_ref || "",
    status: r.status || "active",
  };
}

function mapSimRow(r) {
  return {
    id: r.id,
    number: r.number || "",
    carrier: r.carrier || "",
    simType: r.sim_type || "",
    monthlyPlan: Number(r.monthly_plan) || 0,
    holderEmployeeId: r.holder_employee_id || "",
    holderName: r.holder_name || "",
    activationDate: r.activation_date || "",
    lastBillDate: r.last_bill_date || "",
    status: r.status || "active",
    notes: r.notes || "",
  };
}

function mapVehicleRow(r) {
  return {
    id: r.id,
    plate: r.plate || "",
    brand: r.brand || "",
    model: r.model || "",
    year: Number(r.year) || 0,
    color: r.color || "",
    registrationExpiry: r.registration_expiry || "",
    insuranceExpiry: r.insurance_expiry || "",
    periodicExamExpiry: r.periodic_exam_expiry || "",
    currentDriverId: r.current_driver_id || null,
    currentDriverName: r.current_driver_name || "",
    mileage: Number(r.mileage) || 0,
    status: r.status || "active",
    notes: r.notes || "",
  };
}

function mapMaintenanceRow(r) {
  return {
    id: r.id,
    assetId: r.asset_id || "",
    assetName: r.asset_name || "",
    type: r.type || "",
    reportedDate: r.reported_date || "",
    scheduledDate: r.scheduled_date || "",
    completedDate: r.completed_date || "",
    cost: Number(r.cost) || 0,
    vendor: r.vendor || "",
    technician: r.technician || "",
    description: r.description || "",
    status: r.status || "open",
    notes: r.notes || "",
  };
}

function mapInventoryRow(r) {
  return {
    id: r.id,
    period: r.period || "",
    startDate: r.start_date || "",
    endDate: r.end_date || "",
    conductedBy: r.conducted_by || "",
    totalAssets: Number(r.total_assets) || 0,
    matched: Number(r.matched) || 0,
    discrepancies: Number(r.discrepancies) || 0,
    lostOrMissing: Number(r.lost_or_missing) || 0,
    status: r.status || "planned",
    summary: r.summary || "",
    attachments: r.attachments || "",
  };
}

/* ═════════════════════════════════════════════
   ✦ Constants — categories, statuses, helpers
   ═════════════════════════════════════════════ */
const ASSET_CATEGORIES = [
  { key: "tech",      label: "تقنية",         color: "#0284c7", icon: Smartphone },
  { key: "vehicles",  label: "مركبات",         color: "#7c3aed", icon: Car },
  { key: "furniture", label: "أثاث",          color: "#d97706", icon: Boxes },
  { key: "dawah",     label: "معدات دعوية",   color: C.teal,    icon: Package },
];

const ASSET_STATUSES = [
  { key: "available",   label: "متاح",         color: C.success },
  { key: "in_custody",  label: "قيد العهدة",   color: C.teal },
  { key: "maintenance", label: "تحت الصيانة",  color: C.warning },
  { key: "damaged",     label: "تالف",         color: C.danger },
  { key: "retired",     label: "مستبعد",       color: "#64748b" },
];

const CUSTODY_STATUSES = [
  { key: "active",   label: "نشطة",            color: C.teal },
  { key: "returned", label: "مرتجعة",          color: C.success },
  { key: "overdue",  label: "متأخرة الإرجاع",  color: C.danger },
];

const CUSTODY_CONDITIONS = ["سليم", "جزئي", "تالف"];

const SIM_STATUSES = [
  { key: "active",    label: "نشطة",   color: C.success },
  { key: "suspended", label: "معلقة",  color: C.warning },
  { key: "cancelled", label: "ملغاة",  color: "#64748b" },
];

const VEHICLE_STATUSES = [
  { key: "active",      label: "في الخدمة",   color: C.success },
  { key: "maintenance", label: "تحت الصيانة", color: C.warning },
  { key: "out",         label: "خارج الخدمة", color: C.danger },
];

const MAINTENANCE_TYPES = ["وقائية", "تصحيحية"];
const MAINTENANCE_STATUSES = [
  { key: "open",       label: "مفتوح",       color: C.warning },
  { key: "inprogress", label: "قيد التنفيذ", color: C.teal },
  { key: "done",       label: "مكتمل",       color: C.success },
  { key: "postponed",  label: "مؤجل",        color: "#64748b" },
];

const INVENTORY_STATUSES = [
  { key: "planned",    label: "مخطط", color: "#64748b" },
  { key: "inprogress", label: "جاري", color: C.teal },
  { key: "done",       label: "مكتمل", color: C.success },
  { key: "delayed",    label: "متأخر", color: C.danger },
];

const TODAY_REF = "2026-06-19";

function daysUntil(iso) {
  if (!iso) return null;
  const a = new Date(iso + "T00:00:00");
  const b = new Date(TODAY_REF + "T00:00:00");
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
}

function expiryColor(iso) {
  const d = daysUntil(iso);
  if (d === null) return C.textLight;
  if (d < 0)   return C.danger;
  if (d <= 30) return "#ea580c";
  if (d <= 90) return C.accent;
  return C.success;
}

function expiryLabel(iso) {
  const d = daysUntil(iso);
  if (d === null) return "—";
  if (d < 0)   return `منتهٍ منذ ${Math.abs(d)} يوم`;
  if (d === 0) return "ينتهي اليوم";
  return `بعد ${d} يوم`;
}

/* ═════════════════════════════════════════════
   ✦ Static chart data — حركة العهد شهرياً
   ═════════════════════════════════════════════ */
const HANDOVER_MONTHLY = [];
/* ═════════════════════════════════════════════
   ✦ Helper — count open custody for an employee
   ═════════════════════════════════════════════ */
export function openCustodyCount(employeeId, custody = []) {
  return custody.filter(c => c.employeeId === employeeId && c.status !== "returned").length;
}

/* ═════════════════════════════════════════════
   ✦ Main page
   ═════════════════════════════════════════════ */
export default function AssetsPage({ user, perms }) {
  const [tab, setTab] = useState("overview");
  const safePerms = perms || getPerms(user);

  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(true);
  useEffect(() => { getAssetsAssets().then(({ data }) => { setAssets((data || []).map(mapAssetRow)); setLoadingAssets(false); }).catch(() => setLoadingAssets(false)); }, []);

  const [custody, setCustody] = useState([]);
  const [loadingCustody, setLoadingCustody] = useState(true);
  useEffect(() => { getAssetsCustody().then(({ data }) => { setCustody((data || []).map(mapCustodyRow)); setLoadingCustody(false); }).catch(() => setLoadingCustody(false)); }, []);

  const [sims, setSims] = useState([]);
  const [loadingSims, setLoadingSims] = useState(true);
  useEffect(() => { getAssetsSims().then(({ data }) => { setSims((data || []).map(mapSimRow)); setLoadingSims(false); }).catch(() => setLoadingSims(false)); }, []);

  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  useEffect(() => { getAssetsVehicles().then(({ data }) => { setVehicles((data || []).map(mapVehicleRow)); setLoadingVehicles(false); }).catch(() => setLoadingVehicles(false)); }, []);

  const [maintenance, setMaintenance] = useState([]);
  const [loadingMaintenance, setLoadingMaintenance] = useState(true);
  useEffect(() => { getAssetsMaintenance().then(({ data }) => { setMaintenance((data || []).map(mapMaintenanceRow)); setLoadingMaintenance(false); }).catch(() => setLoadingMaintenance(false)); }, []);

  const [inventory, setInventory] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(true);
  useEffect(() => { getAssetsInventory().then(({ data }) => { setInventory((data || []).map(mapInventoryRow)); setLoadingInventory(false); }).catch(() => setLoadingInventory(false)); }, []);

  const tabs = [
    { key: "overview",    label: "لوحة التحكم",        icon: Activity },
    { key: "assets",      label: "الأصول",             icon: Package },
    { key: "custody",     label: "العهد",              icon: ArrowLeftRight },
    { key: "sims",        label: "الشرائح والاتصالات", icon: Smartphone },
    { key: "vehicles",    label: "السيارات",           icon: Car },
    { key: "maintenance", label: "الصيانة",            icon: Wrench },
    { key: "inventory",   label: "الجرد الدوري",       icon: ClipboardCheck },
    { key: "alerts",      label: "التنبيهات",          icon: Bell },
    { key: "documents",   label: "المحاضر",            icon: FileText },
    { key: "reports",     label: "التقارير",           icon: BarChart3 },
    { key: "meetings",    label: "الاجتماعات",         icon: Calendar },
    { key: "transactions",label: "المعاملات المالية",  icon: DollarSign },
  ];

  const reportSources = [
    {
      label: "العُهد", mode: "monthly", dateField: "handoverDate", rows: custody,
      columns: [
        { key: "assetName", label: "الأصل" },
        { key: "employeeName", label: "الموظف" },
        { key: "handoverDate", label: "تاريخ التسليم" },
        { key: "expectedReturnDate", label: "الإرجاع المتوقع" },
        { key: "status", label: "الحالة" },
      ],
    },
    {
      label: "الصيانة", mode: "monthly", dateField: "reportedDate", rows: maintenance,
      columns: [
        { key: "assetName", label: "الأصل" },
        { key: "type", label: "النوع" },
        { key: "reportedDate", label: "تاريخ البلاغ" },
        { key: "cost", label: "التكلفة" },
        { key: "vendor", label: "المورّد" },
        { key: "status", label: "الحالة" },
      ],
    },
    {
      label: "الجرد الدوري", mode: "monthly", dateField: "startDate", rows: inventory,
      columns: [
        { key: "period", label: "الفترة" },
        { key: "startDate", label: "تاريخ البدء" },
        { key: "conductedBy", label: "نفّذها" },
        { key: "totalAssets", label: "إجمالي الأصول" },
        { key: "matched", label: "المطابق" },
        { key: "status", label: "الحالة" },
      ],
    },
    { label: "الأصول", mode: "cumulative", rows: assets },
    { label: "الشرائح", mode: "cumulative", rows: sims },
    { label: "المركبات", mode: "cumulative", rows: vehicles },
  ];

  return (
    <div>
      <ModuleHeader icon={Package} title="الأصول والعُهد" subtitle="إدارة شاملة للأصول، العهد، السيارات، الشرائح، الصيانة، والجرد الدوري">
        <MonthlyReportButton moduleTitle="الأصول والعُهد" sources={reportSources} budget={350000} />
      </ModuleHeader>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "overview"     && <OverviewTab onJump={setTab} assets={assets} vehicles={vehicles} sims={sims} maintenance={maintenance} custody={custody} />}
      {tab === "assets"       && <AssetsTab canEdit={safePerms.canEdit} assets={assets} setAssets={setAssets} loading={loadingAssets} />}
      {tab === "custody"      && <CustodyTab canEdit={safePerms.canEdit} custody={custody} setCustody={setCustody} loading={loadingCustody} assets={assets} />}
      {tab === "sims"         && <SimsTab canEdit={safePerms.canEdit} sims={sims} setSims={setSims} loading={loadingSims} />}
      {tab === "vehicles"     && <VehiclesTab canEdit={safePerms.canEdit} vehicles={vehicles} setVehicles={setVehicles} loading={loadingVehicles} />}
      {tab === "maintenance"  && <MaintenanceTab canEdit={safePerms.canEdit} maintenance={maintenance} setMaintenance={setMaintenance} loading={loadingMaintenance} />}
      {tab === "inventory"    && <InventoryTab canEdit={safePerms.canEdit} inventory={inventory} setInventory={setInventory} loading={loadingInventory} />}
      {tab === "alerts"       && <AlertsTab onJump={setTab} assets={assets} vehicles={vehicles} custody={custody} />}
      {tab === "documents"    && <DocumentsTab custody={custody} />}
      {tab === "reports"      && <ReportsTab />}
      {tab === "meetings"     && <MeetingsTab departmentName="الأصول والعُهد" module="assets" />}
      {tab === "transactions" && <TransactionsTab budget={350000} module="assets" />}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Overview Tab
   ───────────────────────────────────────────── */
function OverviewTab({ onJump, assets, vehicles, sims, maintenance, custody }) {
  const totalAssets    = assets.length;
  const inCustody      = assets.filter(a => a.status === "in_custody").length;
  const available      = assets.filter(a => a.status === "available").length;
  const totalVehicles  = vehicles.length;
  const activeSims     = sims.filter(s => s.status === "active").length;
  const openMaint      = maintenance.filter(m => m.status !== "done").length;
  const totalValue     = assets.reduce((s, a) => s + (Number(a.purchasePrice) || 0), 0);

  const expiringAlerts = useMemo(() => {
    let n = 0;
    assets.forEach(a => { const d = daysUntil(a.warrantyEnd); if (d !== null && d >= 0 && d <= 60) n++; });
    vehicles.forEach(v => {
      [v.registrationExpiry, v.insuranceExpiry, v.periodicExamExpiry].forEach(iso => {
        const d = daysUntil(iso); if (d !== null && d >= 0 && d <= 60) n++;
      });
    });
    custody.forEach(c => { if (c.status === "overdue") n++; });
    return n;
  }, [assets, vehicles, custody]);

  const categoryData = ASSET_CATEGORIES.map(cat => ({
    name:  cat.label,
    value: assets.filter(a => a.category === cat.key).length,
    color: cat.color,
  }));

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الأصول"        value={totalAssets}    icon={Package}        color={C.primary} />
        <KPICard title="قيد العهدة"           value={inCustody}      icon={ArrowLeftRight} color={C.teal} />
        <KPICard title="الأصول الشاغرة"        value={available}      icon={CheckCircle}    color={C.success} />
        <KPICard title="المركبات"              value={totalVehicles}  icon={Car}            color="#7c3aed" />
        <KPICard title="الشرائح النشطة"        value={activeSims}     icon={Smartphone}     color="#0284c7" />
        <KPICard title="قرب الانتهاء"          value={expiringAlerts} icon={AlertTriangle}  color={C.warning} />
        <KPICard title="مهام صيانة مفتوحة"     value={openMaint}      icon={Wrench}         color={C.accent} />
        <KPICard title="قيمة الأصول الإجمالية" value={`${(totalValue/1000).toFixed(0)}K`} icon={DollarSign} color="#16a34a" subtitle="ريال" />
      </div>

      <div style={styles.chartsRow}>
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={styles.chartTitleWrap}>
              <PieChartIcon size={18} color="#7c3aed" />
              <span style={styles.chartTitle}>توزيع الأصول حسب الفئة</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={categoryData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3} label={false} labelLine={false}>
                {categoryData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
              </Pie>
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v} أصل`]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {categoryData.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ ...styles.legendDot, background: d.color }} />
                <span style={styles.legendLabel}>{d.name}</span>
                <span style={styles.legendValue}>{d.value}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={styles.chartTitleWrap}>
              <BarChart3 size={18} color={C.teal} />
              <span style={styles.chartTitle}>حركة العهد شهرياً</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={HANDOVER_MONTHLY} barGap={6}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }} />
              <Legend wrapperStyle={{ fontSize: 12, fontFamily: "'Readex Pro'" }} />
              <Bar dataKey="handovers" name="تسليم" fill={C.teal}   radius={[6, 6, 0, 0]} />
              <Bar dataKey="returns"   name="استلام" fill={C.accent} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card style={{ padding: 18, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><AlertTriangle size={18} color={C.warning} /> روابط سريعة</h4>
        <div style={styles.quickLinks}>
          <button style={styles.quickBtn} onClick={() => onJump("alerts")}>
            <Bell size={18} color={C.danger} /><span>عرض جميع التنبيهات</span>
          </button>
          <button style={styles.quickBtn} onClick={() => onJump("custody")}>
            <ArrowLeftRight size={18} color={C.teal} /><span>إدارة العهد</span>
          </button>
          <button style={styles.quickBtn} onClick={() => onJump("vehicles")}>
            <Car size={18} color="#7c3aed" /><span>وثائق المركبات</span>
          </button>
          <button style={styles.quickBtn} onClick={() => onJump("maintenance")}>
            <Wrench size={18} color={C.accent} /><span>مهام الصيانة</span>
          </button>
        </div>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Assets Tab
   ───────────────────────────────────────────── */
const INITIAL_ASSET = {
  code: "", name: "", category: "tech", brand: "", model: "", serialNumber: "",
  purchaseDate: "", purchasePrice: "", warrantyEnd: "", location: "",
  status: "available", currentHolder: "", notes: "",
};

function AssetsTab({ canEdit, assets, setAssets, loading }) {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_ASSET);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.code || !form.name || !form.category) {
      showToast("الرمز والاسم والفئة مطلوبة", "error"); return;
    }
    const payload = {
      code: form.code,
      name: form.name,
      category: form.category,
      brand: form.brand || "",
      model: form.model || "",
      serial_number: form.serialNumber || "",
      purchase_date: form.purchaseDate || null,
      purchase_price: Number(form.purchasePrice) || 0,
      warranty_end: form.warrantyEnd || null,
      location: form.location || "",
      status: form.status || "available",
      current_holder: form.currentHolder || null,
      notes: form.notes || "",
    };
    const { data, error } = await createAssetsAsset(payload);
    if (error) { showToast(error || "تعذّر حفظ الأصل", "error"); return; }
    setAssets(prev => [mapAssetRow(data), ...prev]);
    setForm(INITIAL_ASSET);
    setShowAdd(false);
    showToast("تم إضافة الأصل");
  }

  const filtered = assets.filter(a => {
    const matchS = a.name.includes(search) || a.code.includes(search) || (a.brand || "").includes(search);
    const matchC = filterCat === "all" || a.category === filterCat;
    const matchSt = filterStatus === "all" || a.status === filterStatus;
    return matchS && matchC && matchSt;
  });

  function catLabel(key) { return ASSET_CATEGORIES.find(c => c.key === key)?.label || key; }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم/الرمز/الماركة..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={styles.filterGroup}>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الفئات</option>
              {ASSET_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {ASSET_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة أصل</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "code", label: "الرمز", width: "110px", render: v => <span style={styles.monoBold}>{v}</span> },
            { key: "name", label: "الاسم", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "category", label: "الفئة", width: "120px", render: v => catLabel(v) },
            { key: "brand", label: "الماركة", width: "110px" },
            { key: "location", label: "الموقع", width: "180px" },
            { key: "purchasePrice", label: "السعر", width: "100px", render: v => `${(v || 0).toLocaleString()} ر` },
            { key: "warrantyEnd", label: "نهاية الضمان", width: "140px", render: v => v ? <span style={{ color: expiryColor(v), fontWeight: 600 }}>{v}</span> : "—" },
            { key: "status", label: "الحالة", width: "120px", render: v => <StatusBadge status={v} statuses={ASSET_STATUSES} /> },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={`أصل — ${selected.name}`} onClose={() => setSelected(null)} width={620}>
          <div style={styles.detailGrid}>
            <div style={styles.detailRow}><strong>الرمز:</strong> <span style={styles.monoBold}>{selected.code}</span></div>
            <div style={styles.detailRow}><strong>الفئة:</strong> {catLabel(selected.category)}</div>
            <div style={styles.detailRow}><strong>الماركة:</strong> {selected.brand || "—"}</div>
            <div style={styles.detailRow}><strong>الموديل:</strong> {selected.model || "—"}</div>
            <div style={styles.detailRow}><strong>الرقم التسلسلي:</strong> <span style={{ fontFamily: "monospace" }}>{selected.serialNumber || "—"}</span></div>
            <div style={styles.detailRow}><strong>تاريخ الشراء:</strong> {selected.purchaseDate || "—"}</div>
            <div style={styles.detailRow}><strong>سعر الشراء:</strong> {(selected.purchasePrice || 0).toLocaleString()} ريال</div>
            <div style={styles.detailRow}><strong>نهاية الضمان:</strong> {selected.warrantyEnd ? <span style={{ color: expiryColor(selected.warrantyEnd), fontWeight: 600 }}>{selected.warrantyEnd} ({expiryLabel(selected.warrantyEnd)})</span> : "—"}</div>
            <div style={styles.detailRow}><strong>الموقع:</strong> {selected.location || "—"}</div>
            <div style={styles.detailRow}><strong>الحالة:</strong> <StatusBadge status={selected.status} statuses={ASSET_STATUSES} /></div>
            <div style={styles.detailRow}><strong>الحامل الحالي:</strong> {selected.currentHolder || "لا أحد"}</div>
            {selected.notes && <div style={{ ...styles.detailRow, gridColumn: "1 / -1" }}><strong>ملاحظات:</strong> {selected.notes}</div>}
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة أصل جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_ASSET); }} width={680}>
          <div style={styles.formCol}>
            <div style={styles.grid2}>
              <Field label="الرمز" required placeholder="AST-T-XXX" value={form.code} onChange={v => setForm({ ...form, code: v })} />
              <Field label="الاسم" required placeholder="اسم الأصل" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            </div>
            <div style={styles.grid3}>
              <Field label="الفئة" type="select" required options={ASSET_CATEGORIES.map(c => ({ value: c.key, label: c.label }))} value={form.category} onChange={v => setForm({ ...form, category: v })} />
              <Field label="الماركة" value={form.brand} onChange={v => setForm({ ...form, brand: v })} />
              <Field label="الموديل" value={form.model} onChange={v => setForm({ ...form, model: v })} />
            </div>
            <div style={styles.grid3}>
              <Field label="الرقم التسلسلي" value={form.serialNumber} onChange={v => setForm({ ...form, serialNumber: v })} />
              <Field label="تاريخ الشراء" type="date" value={form.purchaseDate} onChange={v => setForm({ ...form, purchaseDate: v })} />
              <Field label="سعر الشراء (ر.س)" type="number" value={form.purchasePrice} onChange={v => setForm({ ...form, purchasePrice: v })} />
            </div>
            <div style={styles.grid3}>
              <Field label="نهاية الضمان" type="date" value={form.warrantyEnd} onChange={v => setForm({ ...form, warrantyEnd: v })} />
              <Field label="الموقع" value={form.location} onChange={v => setForm({ ...form, location: v })} />
              <Field label="الحالة" type="select" options={ASSET_STATUSES.map(s => ({ value: s.key, label: s.label }))} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={styles.formActions}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_ASSET); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Custody Tab
   ───────────────────────────────────────────── */
const INITIAL_HANDOVER = {
  assetId: "", employeeName: "", employeeId: "",
  handoverDate: "", expectedReturnDate: "", condition: "سليم",
  handoverDocRef: "", notes: "",
};
const INITIAL_RETURN = { custodyId: "", actualReturnDate: "", condition: "سليم", returnDocRef: "" };

function CustodyTab({ canEdit, custody, setCustody, loading, assets }) {
  const [filterStatus, setFilterStatus] = useState("all");
  const [showHandover, setShowHandover] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [hForm, setHForm] = useState(INITIAL_HANDOVER);
  const [rForm, setRForm] = useState(INITIAL_RETURN);
  const [showToast, ToastView] = useToast();

  const availableAssets = assets.filter(a => a.status === "available");
  const activeCustody = custody.filter(c => c.status !== "returned");

  async function handleSaveHandover() {
    if (!hForm.assetId || !hForm.employeeName || !hForm.handoverDate) {
      showToast("الأصل والموظف وتاريخ التسليم مطلوبة", "error"); return;
    }
    const asset = assets.find(a => a.id === hForm.assetId);
    const payload = {
      asset_id: hForm.assetId,
      asset_name: asset?.name || "",
      employee_id: hForm.employeeId || "",
      employee_name: hForm.employeeName,
      handover_date: hForm.handoverDate,
      expected_return_date: hForm.expectedReturnDate || null,
      actual_return_date: null,
      condition: hForm.condition || "سليم",
      handover_doc_ref: hForm.handoverDocRef || `HD-${Date.now().toString().slice(-6)}`,
      return_doc_ref: "",
      status: "active",
    };
    const { data, error } = await createAssetsCustody(payload);
    if (error) { showToast(error || "تعذّر تسليم العهدة", "error"); return; }
    setCustody(prev => [mapCustodyRow(data), ...prev]);
    setHForm(INITIAL_HANDOVER);
    setShowHandover(false);
    showToast("تم تسليم العهدة");
  }

  async function handleSaveReturn() {
    if (!rForm.custodyId || !rForm.actualReturnDate) {
      showToast("اختر العهدة وتاريخ الاستلام", "error"); return;
    }
    const patch = {
      actual_return_date: rForm.actualReturnDate,
      condition: rForm.condition,
      return_doc_ref: rForm.returnDocRef || `RT-${Date.now().toString().slice(-6)}`,
      status: "returned",
    };
    const { data, error } = await updateAssetsCustody(rForm.custodyId, patch);
    if (error) { showToast(error || "تعذّر استلام العهدة", "error"); return; }
    setCustody(prev => prev.map(c => c.id === rForm.custodyId ? mapCustodyRow(data) : c));
    setRForm(INITIAL_RETURN);
    setShowReturn(false);
    showToast("تم استلام العهدة");
  }

  const filtered = custody.filter(c => filterStatus === "all" || c.status === filterStatus);

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي العهد"   value={custody.length}                                       icon={ArrowLeftRight} color={C.primary} />
        <KPICard title="نشطة"           value={custody.filter(c => c.status === "active").length}    icon={CheckCircle}    color={C.teal} />
        <KPICard title="مرتجعة"          value={custody.filter(c => c.status === "returned").length} icon={Award}          color={C.success} />
        <KPICard title="متأخرة الإرجاع" value={custody.filter(c => c.status === "overdue").length}   icon={AlertTriangle}  color={C.danger} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع الحالات</option>
            {CUSTODY_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {canEdit && (
            <div style={styles.btnRow}>
              <Btn icon={Plus} onClick={() => setShowHandover(true)} size="sm">تسليم عهدة</Btn>
              <Btn icon={RefreshCw} variant="teal" onClick={() => setShowReturn(true)} size="sm">استلام عهدة</Btn>
            </div>
          )}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "assetName",          label: "الأصل", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "employeeName",       label: "الموظف", width: "180px" },
            { key: "handoverDate",       label: "تاريخ التسليم", width: "120px" },
            { key: "expectedReturnDate", label: "الإرجاع المتوقع", width: "130px" },
            { key: "actualReturnDate",   label: "الإرجاع الفعلي", width: "130px", render: v => v || "—" },
            { key: "condition",          label: "الحالة الفنية", width: "100px" },
            { key: "handoverDocRef",     label: "محضر التسليم", width: "140px", render: v => <span style={styles.monoBold}>{v}</span> },
            { key: "status",             label: "الحالة", width: "130px", render: v => <StatusBadge status={v} statuses={CUSTODY_STATUSES} /> },
          ]}
          data={filtered}
        />
      </Card>

      {showHandover && (
        <Modal title="تسليم عهدة جديدة" onClose={() => { setShowHandover(false); setHForm(INITIAL_HANDOVER); }} width={620}>
          <div style={styles.formCol}>
            <Field label="الأصل" type="select" required options={availableAssets.map(a => ({ value: a.id, label: `${a.code} — ${a.name}` }))} value={hForm.assetId} onChange={v => setHForm({ ...hForm, assetId: v })} />
            <div style={styles.grid2}>
              <Field label="اسم الموظف" required value={hForm.employeeName} onChange={v => setHForm({ ...hForm, employeeName: v })} />
              <Field label="معرف الموظف (empX)" value={hForm.employeeId} onChange={v => setHForm({ ...hForm, employeeId: v })} />
            </div>
            <div style={styles.grid3}>
              <Field label="تاريخ التسليم" type="date" required value={hForm.handoverDate} onChange={v => setHForm({ ...hForm, handoverDate: v })} />
              <Field label="الإرجاع المتوقع" type="date" value={hForm.expectedReturnDate} onChange={v => setHForm({ ...hForm, expectedReturnDate: v })} />
              <Field label="الحالة الفنية" type="select" options={CUSTODY_CONDITIONS} value={hForm.condition} onChange={v => setHForm({ ...hForm, condition: v })} />
            </div>
            <Field label="رقم محضر التسليم" placeholder="سيتم توليده تلقائياً إن فُرغ" value={hForm.handoverDocRef} onChange={v => setHForm({ ...hForm, handoverDocRef: v })} />
            <Field label="ملاحظات" type="textarea" rows={2} value={hForm.notes} onChange={v => setHForm({ ...hForm, notes: v })} />
            <div style={styles.formActions}>
              <Btn icon={ArrowLeftRight} onClick={handleSaveHandover}>تأكيد التسليم</Btn>
              <Btn variant="ghost" onClick={() => { setShowHandover(false); setHForm(INITIAL_HANDOVER); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}

      {showReturn && (
        <Modal title="استلام عهدة" onClose={() => { setShowReturn(false); setRForm(INITIAL_RETURN); }} width={560}>
          <div style={styles.formCol}>
            <Field label="العهدة" type="select" required options={activeCustody.map(c => ({ value: c.id, label: `${c.assetName} — ${c.employeeName}` }))} value={rForm.custodyId} onChange={v => setRForm({ ...rForm, custodyId: v })} />
            <div style={styles.grid2}>
              <Field label="تاريخ الاستلام" type="date" required value={rForm.actualReturnDate} onChange={v => setRForm({ ...rForm, actualReturnDate: v })} />
              <Field label="الحالة الفنية" type="select" options={CUSTODY_CONDITIONS} value={rForm.condition} onChange={v => setRForm({ ...rForm, condition: v })} />
            </div>
            <Field label="رقم محضر الاستلام" placeholder="سيتم توليده تلقائياً إن فُرغ" value={rForm.returnDocRef} onChange={v => setRForm({ ...rForm, returnDocRef: v })} />
            <div style={styles.formActions}>
              <Btn icon={RefreshCw} variant="teal" onClick={handleSaveReturn}>تأكيد الاستلام</Btn>
              <Btn variant="ghost" onClick={() => { setShowReturn(false); setRForm(INITIAL_RETURN); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ SIMs Tab
   ───────────────────────────────────────────── */
function SimsTab({ canEdit, sims }) {
  const [filterStatus, setFilterStatus] = useState("all");

  const filtered = sims.filter(s => filterStatus === "all" || s.status === filterStatus);
  const monthlyTotal = sims.filter(s => s.status === "active").reduce((a, b) => a + (b.monthlyPlan || 0), 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الشرائح" value={sims.length}                                          icon={Smartphone} color="#0284c7" />
        <KPICard title="نشطة"           value={sims.filter(s => s.status === "active").length}      icon={CheckCircle} color={C.success} />
        <KPICard title="معلقة"           value={sims.filter(s => s.status === "suspended").length}  icon={Clock}       color={C.warning} />
        <KPICard title="فاتورة شهرية"   value={`${monthlyTotal.toLocaleString()} ر`}                icon={DollarSign}  color={C.teal} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع الحالات</option>
            {SIM_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {canEdit && <Btn icon={Plus} size="sm">إضافة شريحة</Btn>}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "number",       label: "الرقم", width: "130px", render: v => <span style={styles.monoBold} dir="ltr">{v}</span> },
            { key: "carrier",      label: "المشغل", width: "100px" },
            { key: "simType",      label: "النوع", width: "100px" },
            { key: "monthlyPlan",  label: "الباقة الشهرية", width: "120px", render: v => `${v} ر` },
            { key: "holderName",   label: "الحامل" },
            { key: "activationDate", label: "تاريخ التفعيل", width: "120px" },
            { key: "lastBillDate", label: "آخر فاتورة", width: "120px" },
            { key: "status",       label: "الحالة", width: "100px", render: v => <StatusBadge status={v} statuses={SIM_STATUSES} /> },
          ]}
          data={filtered}
        />
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Vehicles Tab
   ───────────────────────────────────────────── */
function VehiclesTab({ canEdit, vehicles }) {
  const [selected, setSelected] = useState(null);

  const expiringSoon = vehicles.filter(v => {
    return [v.registrationExpiry, v.insuranceExpiry, v.periodicExamExpiry].some(iso => {
      const d = daysUntil(iso); return d !== null && d <= 90;
    });
  }).length;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المركبات" value={vehicles.length}                                      icon={Car}           color="#7c3aed" />
        <KPICard title="في الخدمة"       value={vehicles.filter(v => v.status === "active").length}  icon={CheckCircle}   color={C.success} />
        <KPICard title="تحت الصيانة"     value={vehicles.filter(v => v.status === "maintenance").length} icon={Wrench}     color={C.warning} />
        <KPICard title="وثائق قرب الانتهاء" value={expiringSoon}                                      icon={AlertTriangle} color={C.danger} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13, color: C.textLight }}>ألوان التواريخ: <span style={{ color: C.success, fontWeight: 700 }}>● ساري</span> · <span style={{ color: C.accent, fontWeight: 700 }}>● ≤90 يوم</span> · <span style={{ color: "#ea580c", fontWeight: 700 }}>● ≤30 يوم</span> · <span style={{ color: C.danger, fontWeight: 700 }}>● منتهٍ</span></span>
        {canEdit && <Btn icon={Plus} size="sm">إضافة مركبة</Btn>}
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "plate",                label: "اللوحة", width: "120px", render: v => <span style={styles.monoBold}>{v}</span> },
            { key: "brand",                label: "الماركة", width: "110px" },
            { key: "model",                label: "الموديل", width: "130px" },
            { key: "year",                 label: "السنة", width: "70px" },
            { key: "registrationExpiry",   label: "نهاية الاستمارة", width: "150px", render: v => <span style={{ color: expiryColor(v), fontWeight: 700 }}>{v} · {expiryLabel(v)}</span> },
            { key: "insuranceExpiry",      label: "نهاية التأمين", width: "150px",   render: v => <span style={{ color: expiryColor(v), fontWeight: 700 }}>{v} · {expiryLabel(v)}</span> },
            { key: "periodicExamExpiry",   label: "نهاية الفحص الدوري", width: "160px", render: v => <span style={{ color: expiryColor(v), fontWeight: 700 }}>{v} · {expiryLabel(v)}</span> },
            { key: "mileage",              label: "الكيلومترات", width: "110px", render: v => `${(v || 0).toLocaleString()} كم` },
            { key: "status",               label: "الحالة", width: "110px", render: v => <StatusBadge status={v} statuses={VEHICLE_STATUSES} /> },
          ]}
          data={vehicles}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={`مركبة — ${selected.plate}`} onClose={() => setSelected(null)} width={620}>
          <div style={styles.detailGrid}>
            <div style={styles.detailRow}><strong>اللوحة:</strong> <span style={styles.monoBold}>{selected.plate}</span></div>
            <div style={styles.detailRow}><strong>الماركة:</strong> {selected.brand}</div>
            <div style={styles.detailRow}><strong>الموديل:</strong> {selected.model}</div>
            <div style={styles.detailRow}><strong>السنة:</strong> {selected.year}</div>
            <div style={styles.detailRow}><strong>اللون:</strong> {selected.color}</div>
            <div style={styles.detailRow}><strong>السائق:</strong> {selected.currentDriverName || "غير محدد"}</div>
            <div style={styles.detailRow}><strong>الكيلومترات:</strong> {(selected.mileage || 0).toLocaleString()} كم</div>
            <div style={styles.detailRow}><strong>الاستمارة:</strong> <span style={{ color: expiryColor(selected.registrationExpiry), fontWeight: 700 }}>{selected.registrationExpiry} ({expiryLabel(selected.registrationExpiry)})</span></div>
            <div style={styles.detailRow}><strong>التأمين:</strong> <span style={{ color: expiryColor(selected.insuranceExpiry), fontWeight: 700 }}>{selected.insuranceExpiry} ({expiryLabel(selected.insuranceExpiry)})</span></div>
            <div style={styles.detailRow}><strong>الفحص الدوري:</strong> <span style={{ color: expiryColor(selected.periodicExamExpiry), fontWeight: 700 }}>{selected.periodicExamExpiry} ({expiryLabel(selected.periodicExamExpiry)})</span></div>
            <div style={styles.detailRow}><strong>الحالة:</strong> <StatusBadge status={selected.status} statuses={VEHICLE_STATUSES} /></div>
            {selected.notes && <div style={{ ...styles.detailRow, gridColumn: "1 / -1" }}><strong>ملاحظات:</strong> {selected.notes}</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Maintenance Tab
   ───────────────────────────────────────────── */
function MaintenanceTab({ canEdit, maintenance }) {
  const items = maintenance;
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");

  const filtered = items.filter(m =>
    (filterStatus === "all" || m.status === filterStatus) &&
    (filterType === "all" || m.type === filterType)
  );
  const totalCost = items.reduce((s, m) => s + (Number(m.cost) || 0), 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الطلبات" value={items.length}                                          icon={Wrench}        color={C.primary} />
        <KPICard title="مفتوحة"          value={items.filter(m => m.status === "open").length}        icon={Clock}         color={C.warning} />
        <KPICard title="قيد التنفيذ"     value={items.filter(m => m.status === "inprogress").length}  icon={Activity}      color={C.teal} />
        <KPICard title="إجمالي التكاليف" value={`${totalCost.toLocaleString()} ر`}                    icon={DollarSign}    color={C.success} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.filterGroup}>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأنواع</option>
              {MAINTENANCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {MAINTENANCE_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          {canEdit && <Btn icon={Plus} size="sm">طلب صيانة</Btn>}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "assetName",     label: "الأصل", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "type",          label: "النوع", width: "100px" },
            { key: "reportedDate",  label: "تاريخ البلاغ", width: "120px" },
            { key: "scheduledDate", label: "التاريخ المجدول", width: "130px" },
            { key: "completedDate", label: "تاريخ الإنجاز", width: "120px", render: v => v || "—" },
            { key: "vendor",        label: "المُورّد", width: "140px" },
            { key: "technician",    label: "الفني", width: "120px" },
            { key: "cost",          label: "التكلفة", width: "100px", render: v => v ? `${v.toLocaleString()} ر` : "—" },
            { key: "status",        label: "الحالة", width: "120px", render: v => <StatusBadge status={v} statuses={MAINTENANCE_STATUSES} /> },
          ]}
          data={filtered}
        />
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Inventory Tab
   ───────────────────────────────────────────── */
function InventoryTab({ canEdit, inventory }) {
  const items = inventory;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="عمليات الجرد"  value={items.length}                                       icon={ClipboardCheck} color={C.primary} />
        <KPICard title="مكتملة"         value={items.filter(i => i.status === "done").length}     icon={CheckCircle}    color={C.success} />
        <KPICard title="جارية"          value={items.filter(i => i.status === "inprogress").length} icon={Activity}     color={C.teal} />
        <KPICard title="مخططة"          value={items.filter(i => i.status === "planned").length}  icon={Calendar}       color={C.warning} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
        {canEdit && <Btn icon={Plus} size="sm">جرد جديد</Btn>}
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "period",         label: "نوع الجرد", width: "110px" },
            { key: "startDate",      label: "البداية", width: "120px" },
            { key: "endDate",        label: "النهاية", width: "120px" },
            { key: "conductedBy",    label: "نفذه", width: "200px" },
            { key: "totalAssets",    label: "إجمالي الأصول", width: "110px" },
            { key: "matched",        label: "متطابق", width: "80px", render: v => <span style={{ color: C.success, fontWeight: 700 }}>{v}</span> },
            { key: "discrepancies",  label: "فروقات", width: "80px", render: v => <span style={{ color: v ? C.warning : C.textLight, fontWeight: 700 }}>{v}</span> },
            { key: "lostOrMissing",  label: "مفقود", width: "80px", render: v => <span style={{ color: v ? C.danger : C.textLight, fontWeight: 700 }}>{v}</span> },
            { key: "status",         label: "الحالة", width: "110px", render: v => <StatusBadge status={v} statuses={INVENTORY_STATUSES} /> },
            { key: "attachments",    label: "المرفق", width: "140px", render: v => v ? <span style={styles.monoBold}>{v}</span> : "—" },
          ]}
          data={items}
        />
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Alerts Tab
   ───────────────────────────────────────────── */
function AlertsTab({ onJump, assets, vehicles, custody }) {
  const warrantyAlerts = assets
    .filter(a => { const d = daysUntil(a.warrantyEnd); return d !== null && d <= 90; })
    .map(a => ({
      key: `wr-${a.id}`, kind: "warranty", title: `انتهاء ضمان: ${a.name}`,
      detail: `الموقع: ${a.location} — ${expiryLabel(a.warrantyEnd)} (${a.warrantyEnd})`,
      color: expiryColor(a.warrantyEnd), icon: ShieldCheck, jumpTo: "assets",
    }));

  const vehicleAlerts = [];
  vehicles.forEach(v => {
    const checks = [
      { field: "التأمين",         iso: v.insuranceExpiry,     icon: ShieldAlert, kind: "insurance" },
      { field: "الاستمارة",        iso: v.registrationExpiry,  icon: FileCheck,   kind: "registration" },
      { field: "الفحص الدوري",      iso: v.periodicExamExpiry,  icon: ClipboardCheck, kind: "exam" },
    ];
    checks.forEach(ch => {
      const d = daysUntil(ch.iso);
      if (d !== null && d <= 90) {
        vehicleAlerts.push({
          key: `vh-${v.id}-${ch.kind}`, kind: ch.kind, title: `انتهاء ${ch.field}: ${v.plate}`,
          detail: `${v.brand} ${v.model} — ${expiryLabel(ch.iso)} (${ch.iso})`,
          color: expiryColor(ch.iso), icon: ch.icon, jumpTo: "vehicles",
        });
      }
    });
  });

  const overdueAlerts = custody
    .filter(c => c.status === "overdue")
    .map(c => ({
      key: `cu-${c.id}`, kind: "overdue", title: `عهدة متأخرة: ${c.assetName}`,
      detail: `الموظف: ${c.employeeName} — الإرجاع المتوقع: ${c.expectedReturnDate}`,
      color: C.danger, icon: AlertTriangle, jumpTo: "custody",
    }));

  const all = [...warrantyAlerts, ...vehicleAlerts, ...overdueAlerts];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي التنبيهات" value={all.length}              icon={Bell}            color={C.danger} />
        <KPICard title="ضمان قريب"          value={warrantyAlerts.length}  icon={ShieldCheck}     color={C.warning} />
        <KPICard title="وثائق المركبات"     value={vehicleAlerts.length}   icon={Car}             color="#7c3aed" />
        <KPICard title="عهد متأخرة"          value={overdueAlerts.length}  icon={ArrowLeftRight}  color={C.danger} />
      </div>

      <div style={styles.alertsGrid}>
        {all.map(a => {
          const Ic = a.icon;
          return (
            <Card key={a.key} style={{ padding: 16, borderRight: `4px solid ${a.color}` }}>
              <div style={styles.alertHead}>
                <div style={{ ...styles.alertIcon, background: `${a.color}15` }}>
                  <Ic size={20} color={a.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={styles.alertTitle}>{a.title}</p>
                  <p style={styles.alertDetail}>{a.detail}</p>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <Btn variant="ghost" size="sm" icon={Eye} onClick={() => onJump(a.jumpTo)}>
                  {a.kind === "overdue" ? "عرض العهدة" : "عرض الأصل"}
                </Btn>
              </div>
            </Card>
          );
        })}
        {all.length === 0 && (
          <Card style={{ padding: 28, textAlign: "center", gridColumn: "1 / -1" }}>
            <CheckCircle size={36} color={C.success} />
            <p style={{ margin: "8px 0 0", color: C.success, fontWeight: 700 }}>لا توجد تنبيهات حالياً</p>
          </Card>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Documents Tab
   ───────────────────────────────────────────── */
function DocumentsTab({ custody }) {
  const docs = useMemo(() => {
    const out = [];
    custody.forEach(c => {
      if (c.handoverDocRef) {
        out.push({ id: `H-${c.id}`, type: "تسليم",  date: c.handoverDate,    employeeName: c.employeeName, assetName: c.assetName, docNumber: c.handoverDocRef, pdfRef: `${c.handoverDocRef}.pdf` });
      }
      if (c.returnDocRef) {
        out.push({ id: `R-${c.id}`, type: "استلام", date: c.actualReturnDate, employeeName: c.employeeName, assetName: c.assetName, docNumber: c.returnDocRef,   pdfRef: `${c.returnDocRef}.pdf` });
      }
    });
    return out.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [custody]);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المحاضر" value={docs.length}                                  icon={FileText}  color={C.primary} />
        <KPICard title="محاضر تسليم"     value={docs.filter(d => d.type === "تسليم").length}  icon={ArrowLeftRight} color={C.teal} />
        <KPICard title="محاضر استلام"    value={docs.filter(d => d.type === "استلام").length} icon={RefreshCw} color={C.success} />
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "docNumber",    label: "رقم المحضر", width: "150px", render: v => <span style={styles.monoBold}>{v}</span> },
            { key: "type",         label: "النوع", width: "100px", render: v => <span style={{ fontWeight: 700, color: v === "تسليم" ? C.teal : C.success }}>{v}</span> },
            { key: "date",         label: "التاريخ", width: "120px" },
            { key: "employeeName", label: "الموظف", width: "200px" },
            { key: "assetName",    label: "الأصل" },
            { key: "pdfRef",       label: "إجراءات", width: "190px", render: () => (
              <div style={styles.docActions}>
                <Btn variant="ghost" size="sm" icon={Printer}>طباعة</Btn>
                <Btn variant="ghost" size="sm" icon={Download}>تنزيل</Btn>
              </div>
            )},
          ]}
          data={docs}
        />
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Reports Tab
   ───────────────────────────────────────────── */
function ReportsTab() {
  const cards = [
    { title: "تقرير الأصول الشامل",      icon: Package,        color: C.primary, desc: "جرد كامل لجميع الأصول حسب الفئة والموقع والحالة" },
    { title: "تقرير العهد حسب الموظف",  icon: ArrowLeftRight, color: C.teal,    desc: "العهد النشطة والمرتجعة والمتأخرة مفصّلة لكل موظف" },
    { title: "تقرير الصيانة",            icon: Wrench,         color: C.accent,  desc: "إحصاءات الصيانة الوقائية والتصحيحية مع التكاليف" },
    { title: "تقرير الجرد",               icon: ClipboardCheck, color: C.success, desc: "نتائج الجرد الدوري والفروقات والمفقودات" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.reportsGrid}>
        {cards.map((r, i) => {
          const Ic = r.icon;
          return (
            <Card key={i} style={{ padding: 22 }}>
              <div style={styles.reportHead}>
                <div style={{ ...styles.reportIcon, background: `${r.color}15` }}>
                  <Ic size={24} color={r.color} />
                </div>
                <h4 style={styles.reportTitle}>{r.title}</h4>
              </div>
              <p style={styles.reportDesc}>{r.desc}</p>
              <div style={styles.reportActions}>
                <Btn variant="ghost" size="sm" icon={Eye}>عرض</Btn>
                <Btn variant="ghost" size="sm" icon={Download}>تنزيل</Btn>
                <Btn variant="ghost" size="sm" icon={Printer}>طباعة</Btn>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },

  chartsRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 20, marginBottom: 20 },
  chartCard: { padding: 20 },
  chartHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  chartTitleWrap: { display: "flex", alignItems: "center", gap: 8 },
  chartTitle: { fontSize: 15, fontWeight: 700, color: C.primary },
  legendGrid: { display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 8 },
  legendItem: { display: "flex", alignItems: "center", gap: 6 },
  legendDot: { width: 10, height: 10, borderRadius: 3 },
  legendLabel: { fontSize: 11, color: C.textLight },
  legendValue: { fontSize: 11, fontWeight: 600, color: C.primary },

  sectionTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px" },
  quickLinks: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 },
  quickBtn: {
    display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
    background: C.white, border: `1px solid ${C.border}`, borderRadius: 12,
    cursor: "pointer", fontFamily: "'Readex Pro', sans-serif",
    fontSize: 13, fontWeight: 600, color: C.primary,
  },

  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  filterGroup: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  filterSelect: { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer" },
  btnRow: { display: "flex", gap: 8 },

  monoBold: { fontFamily: "monospace", fontWeight: 700, color: C.primary },

  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.7 },

  formCol: { display: "flex", flexDirection: "column", gap: 12 },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 },
  formActions: { display: "flex", gap: 8, marginTop: 8 },

  alertsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 },
  alertHead: { display: "flex", gap: 12, alignItems: "flex-start" },
  alertIcon: { width: 44, height: 44, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  alertTitle: { margin: 0, fontSize: 14, fontWeight: 700, color: C.primary, lineHeight: 1.4 },
  alertDetail: { margin: "4px 0 0", fontSize: 12, color: C.textLight, lineHeight: 1.6 },

  docActions: { display: "flex", gap: 6 },

  reportsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 },
  reportHead: { display: "flex", alignItems: "center", gap: 12, marginBottom: 10 },
  reportIcon: { width: 48, height: 48, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" },
  reportTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: C.primary },
  reportDesc: { fontSize: 12, color: C.textLight, lineHeight: 1.7, margin: "0 0 14px" },
  reportActions: { display: "flex", gap: 8 },
};

